import { PermissionKey } from '../../../utils/apollo/graphqlSchema';
import { useAppSelector } from '../../../redux/hooks';
import { useMemo } from 'react';
import { getPermissionKeyAndSchemaIdsFromRoles } from '../utils';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { DevPermission } from '../../../redux/slices/permission';
import {
  useBootstrapSelector,
  useUserSettingsSelector,
} from '../../../redux/selectorHooks';
import { IS_SCREENSHOT_MODE } from '../../../config/constants';

interface IProps {
  requestedPermissionKey: PermissionKey;
  defaultSchemaId?: string;
}

interface IReturn {
  selectedSchemaId: string;
  // if return empty list, means all schemas are allowed.
  permittedSchemaIds: string[];
}

/**
 * This hook is used to return the available schemas KEYS and default selected schema KEY for a given permission.
 * @param requestedPermissionKey the permission key to check
 * @param defaultSchemaId if provided, the default schema id to select before permission check
 */
const usePermissionSchemas = ({
  requestedPermissionKey,
  defaultSchemaId,
}: IProps): IReturn => {
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const userPermission = useAppSelector((state) => state.permission);
  const { roles, devPermission } = userPermission;
  const defaultSchemaIdUserSetting = useUserSettingsSelector('defaultSchemaId');
  assertNotNil(defaultSchemaIdUserSetting);
  const activeSchemaIdUserSetting = useUserSettingsSelector('activeSchemaId');

  const allPermissionsFromUserRoles = useMemo(() => {
    return getPermissionKeyAndSchemaIdsFromRoles(roles);
  }, [roles]);

  // Special case: if dev permission is enabled or permissionControl is disabled by Kobol for customer,
  // allow all schemas.
  if (
    !permissionConfig.enablePermissionControl ||
    devPermission === DevPermission.ForceEnable ||
    IS_SCREENSHOT_MODE
  ) {
    return {
      selectedSchemaId: defaultSchemaId || defaultSchemaIdUserSetting,
      permittedSchemaIds: [],
    };
  }
  const permissionSchemaIds = allPermissionsFromUserRoles.find(
    (permission) => permission.permissionKey === requestedPermissionKey,
  )?.schemaIds;

  // if not found permission and schemaIds,
  // user should not be able to trigger this hook since not allowed to render component.
  assertCondition(permissionSchemaIds && permissionSchemaIds.length > 0);

  const schemaId =
    defaultSchemaId || activeSchemaIdUserSetting || defaultSchemaIdUserSetting;

  return {
    selectedSchemaId: permissionSchemaIds.find(
      (permissionSchemaId) =>
        permissionSchemaId === schemaId ||
        permissionSchemaId === permissionConfig.allSchema.id,
    )
      ? schemaId
      : permissionSchemaIds[0],
    permittedSchemaIds: permissionSchemaIds.includes(
      permissionConfig.allSchema.id,
    )
      ? []
      : permissionSchemaIds,
  };
};

export default usePermissionSchemas;
